import { loadBaseCSS } from '../shared/button-utils.js';
import { generateControlId, upgradeProperty } from '../shared/form-control.js';
import { enumAttribute, primitiveEvent, primitiveStyles, rovingIndex } from '../shared/primitive-utils.js';
import './qds-tab.js';
import './qds-tab-panel.js';
import type { QdsTab } from './qds-tab.js';
import type { QdsTabPanel } from './qds-tab-panel.js';

export interface QdsTabsChangeDetail { component: QdsTabs; value: string; previousValue: string; }

/**
 * Composable tabs with automatic or manual activation and preserved panel state.
 * @element qds-tabs
 * @attr {string} value - Selected tab identity; defaults to the first enabled tab.
 * @attr {'horizontal'|'vertical'} orientation - Tablist orientation.
 * @attr {'automatic'|'manual'} activation - Whether arrow focus also selects. Default automatic.
 * @attr {string} label - Accessible name of the tablist. Default Tabs.
 * @property {string} value - Selected tab identity.
 * @property {'horizontal'|'vertical'} orientation - Tablist orientation.
 * @property {'automatic'|'manual'} activation - Keyboard activation policy.
 * @slot tab - Direct qds-tab children; the child assigns this slot automatically.
 * @slot panel - Matching qds-tab-panel children; the child assigns this slot automatically.
 * @csspart tablist - The labelled tablist.
 * @fires {{ component: HTMLElement; value: string; previousValue: string }} qds-change - User selection changed. Detail: QdsTabsChangeDetail.
 */
export class QdsTabs extends HTMLElement {
  static get observedAttributes(): string[] { return ['value', 'orientation', 'activation', 'label']; }
  private _observer: MutationObserver;
  private _value = '';
  private _syncing = false;

  constructor() {
    super();
    this.attachShadow({ mode: 'open' }).innerHTML = `<style>${primitiveStyles}
      :host { display: block; min-width: 0; }
      [part="tablist"] { display: flex; overflow: auto; gap: var(--theme-space-1, 4px);
        border-block-end: 1px solid var(--theme-border-primary, #e4e9f2); }
      :host([orientation="vertical"]) { display: grid; grid-template-columns: minmax(8rem, max-content) minmax(0, 1fr); gap: 16px; }
      :host([orientation="vertical"]) [part="tablist"] { flex-direction: column; border-block-end: 0;
        border-inline-end: 1px solid var(--theme-border-primary, #e4e9f2); }
    </style><div part="tablist" role="tablist"><slot name="tab"></slot></div><slot name="panel"></slot>`;
    this._observer = new MutationObserver(() => this._sync());
    loadBaseCSS();
  }
  connectedCallback(): void {
    for (const prop of ['value', 'orientation', 'activation']) upgradeProperty(this, prop);
    this.addEventListener('click', this._onClick);
    this.addEventListener('keydown', this._onKeydown);
    this._observer.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['value', 'disabled', 'slot'] });
    this._sync();
  }
  disconnectedCallback(): void {
    this._observer.disconnect();
    this.removeEventListener('click', this._onClick);
    this.removeEventListener('keydown', this._onKeydown);
  }
  attributeChangedCallback(): void { if (this.isConnected) this._sync(); }
  get value(): string { return this.isConnected ? this._value : this.getAttribute('value') || this._value; }
  set value(value: string) { this.setAttribute('value', value ?? ''); }
  get orientation(): 'horizontal' | 'vertical' { return enumAttribute(this, 'orientation', ['horizontal', 'vertical'], 'horizontal'); }
  set orientation(value: 'horizontal' | 'vertical') { this.setAttribute('orientation', value); }
  get activation(): 'automatic' | 'manual' { return enumAttribute(this, 'activation', ['automatic', 'manual'], 'automatic'); }
  set activation(value: 'automatic' | 'manual') { this.setAttribute('activation', value); }

  private _tabs(): QdsTab[] { return Array.from(this.children).filter((child): child is QdsTab => child.localName === 'qds-tab'); }
  private _panels(): QdsTabPanel[] { return Array.from(this.children).filter((child): child is QdsTabPanel => child.localName === 'qds-tab-panel'); }
  private _key(tab: QdsTab): string { return tab.value || String(this._tabs().indexOf(tab)); }
  private _sync(): void {
    if (this._syncing) return;
    this._syncing = true;
    try {
      const tabs = this._tabs();
      const panels = this._panels();
      const desired = this.getAttribute('value') || this._value;
      const selected = tabs.find(tab => !tab.disabled && this._key(tab) === desired) || tabs.find(tab => !tab.disabled);
      this._value = selected ? this._key(selected) : '';
      const tablist = this.shadowRoot!.querySelector('[role="tablist"]')!;
      tablist.setAttribute('aria-label', this.getAttribute('label') || 'Tabs');
      tablist.setAttribute('aria-orientation', this.orientation);
      tabs.forEach((tab, index) => {
        if (!tab.id) tab.id = generateControlId('tab');
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-selected', String(tab === selected));
        tab.setAttribute('aria-disabled', String(tab.disabled));
        tab.tabIndex = tab === selected ? 0 : -1;
        const panel = panels.find((item, panelIndex) => (item.value || String(panelIndex)) === (tab.value || String(index)));
        if (panel) {
          if (!panel.id) panel.id = generateControlId('tabpanel');
          tab.setAttribute('aria-controls', panel.id);
          panel.setAttribute('aria-labelledby', tab.id);
        } else tab.removeAttribute('aria-controls');
      });
      panels.forEach((panel, index) => { panel.hidden = !selected || (panel.value || String(index)) !== this._value; });
    } finally { this._syncing = false; }
  }
  private _eventTab(event: Event): QdsTab | undefined {
    return this._tabs().find(tab => event.composedPath().includes(tab));
  }
  private _select(tab: QdsTab): void {
    if (tab.disabled) return;
    const previousValue = this.value;
    this.value = this._key(tab);
    if (this.value !== previousValue) primitiveEvent<QdsTabsChangeDetail>(this, 'qds-change', { component: this, value: this.value, previousValue });
  }
  private _onClick = (event: MouseEvent): void => {
    const tab = this._eventTab(event);
    if (!tab || tab.disabled) return;
    this._select(tab); tab.focus();
  };
  private _onKeydown = (event: KeyboardEvent): void => {
    const tab = this._eventTab(event);
    if (!tab || tab.disabled || event.altKey || event.ctrlKey || event.metaKey) return;
    const tabs = this._tabs().filter(item => !item.disabled);
    const index = tabs.indexOf(tab);
    if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); this._select(tab); return; }
    const vertical = this.orientation === 'vertical';
    const rtl = getComputedStyle(this).direction === 'rtl';
    let next: number;
    if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    else if (event.key === (vertical ? 'ArrowDown' : rtl ? 'ArrowLeft' : 'ArrowRight')) next = rovingIndex(index, tabs.length, 1);
    else if (event.key === (vertical ? 'ArrowUp' : rtl ? 'ArrowRight' : 'ArrowLeft')) next = rovingIndex(index, tabs.length, -1);
    else return;
    event.preventDefault();
    if (this.activation === 'automatic') this._select(tabs[next]);
    tabs.forEach((item, itemIndex) => { item.tabIndex = itemIndex === next ? 0 : -1; });
    tabs[next].focus();
  };
}
if (!customElements.get('qds-tabs')) customElements.define('qds-tabs', QdsTabs);
